import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { AuthStateService, Credentials } from './auth-state.service';
import { AuthService } from './auth.service';

type AuthResponse = { data: Credentials; statusCode: number };
type ApiError = { statusCode: number; data: { error: unknown } };

/** Single-flight refresh request shared by all concurrent 401 retries. */
let refreshRequest$: Observable<AuthResponse> | null = null;

const wrapResponseBody = (event: HttpEvent<unknown>): HttpEvent<unknown> => {
  if (event instanceof HttpResponse) {
    return event.clone({
      body: {
        data: event.body,
        statusCode: event.status,
      },
    });
  }

  return event;
};

const isApiError = (err: unknown): err is ApiError =>
  !!err &&
  typeof err === 'object' &&
  'statusCode' in err &&
  typeof err.statusCode === 'number' &&
  'data' in err &&
  !!err.data &&
  typeof err.data === 'object' &&
  'error' in err.data;

const toApiError = (err: unknown): ApiError => {
  if (isApiError(err)) {
    return err;
  }

  const httpError = err as HttpErrorResponse;

  return { statusCode: httpError.status, data: { error: httpError.error } };
};

/** Encode raw URL characters while preserving already encoded path/query octets. */
const encodeUriOnce = (url: string): string =>
  encodeURI(url)
    .replace(/%25([0-9A-Fa-f]{2})/g, '%$1')
    .replace(/%5B/g, '[')
    .replace(/%5D/g, ']')
    .replace(/'/g, '%27')
    .replace(/\+/g, '%2B');

const refreshTokenAndRetry = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authState: AuthStateService,
  authService: AuthService,
): Observable<HttpEvent<unknown>> => {
  if (!refreshRequest$) {
    refreshRequest$ = authService.refreshToken().pipe(
      finalize(() => {
        refreshRequest$ = null;
      }),
      shareReplay(1),
    );
  }

  return refreshRequest$.pipe(
    switchMap(res =>
      next(
        request.clone({
          setHeaders: {
            Authorization: 'Bearer ' + res.data.token,
            'X-Refresh-Retry': '1',
          },
        }),
      ).pipe(map(wrapResponseBody)),
    ),
    catchError((err: unknown) => {
      const apiError = toApiError(err);
      if (apiError.statusCode === 403 || apiError.statusCode === 401) {
        authState.clearCredentials();
      }

      return throwError(() => apiError);
    }),
  );
};

export const authInterceptor: HttpInterceptorFn = (request, next) => {
  const authState = inject(AuthStateService);
  const authService = inject(AuthService);

  const isNoAuthEndpoint = !!authState.noAuthEndPoint.find(e => !!request.url.match(new RegExp(`^${e}`)));
  // Identify the refresh call by its actual path, not a substring of the whole URL (a search term
  // containing "refresh-token" must not cause the refresh credential to be attached to a data request).
  const isRefreshTokenRequest = request.url.split('?')[0].endsWith('/api/auth/refresh-token');

  if (request.url.includes('i18n') || request.url.includes('mat-icons')) {
    return next(request);
  }

  if (!isNoAuthEndpoint) {
    const encodedUri = encodeUriOnce(request.url);
    const token = isRefreshTokenRequest ? authState.credentials?.refreshToken : authState.credentials?.token;

    request = request.clone({
      url: `${environment.apiHost}${encodedUri}`,
      // Omit the header entirely when there is no token, rather than sending "Bearer undefined".
      setHeaders: token ? { Authorization: `Bearer ${token}` } : {},
    });
  } else {
    request = request.clone({
      url: environment.apiHost + request.url,
    });
  }

  return next(request).pipe(
    map(wrapResponseBody),
    catchError((err: HttpErrorResponse) => {
      const isRefreshRequest = isRefreshTokenRequest;
      const alreadyRetried = request.headers.has('X-Refresh-Retry');

      if (err.status === 401 && err.error?.error === 'Authorization token expired' && !isRefreshRequest && !alreadyRetried) {
        return refreshTokenAndRetry(request, next, authState, authService);
      }

      if (err.status === 401 && !isNoAuthEndpoint) {
        authState.clearCredentials();
      }

      return throwError(() => toApiError(err));
    }),
  );
};
